import { Button, Card, Collapse, Empty, Space, Typography } from "antd";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  MinusCircleFilled,
} from "@ant-design/icons";
import { bandFor, bandLabel, tokensFor, type ScoreBand } from "../../lib/scoreColors";
import type {
  ScoringAggregate,
  ScoringEvaluation,
  ScoringRubric,
} from "../intakeDetails/types";

interface Props {
  rubric: ScoringRubric;
  evaluation: ScoringEvaluation;
  aggregate: ScoringAggregate | null;
  onEdit: () => void;
}

function bandIcon(band: ScoreBand) {
  if (band === "green")
    return <CheckCircleFilled aria-hidden style={{ marginRight: 4 }} />;
  if (band === "yellow")
    return <MinusCircleFilled aria-hidden style={{ marginRight: 4 }} />;
  return <ExclamationCircleFilled aria-hidden style={{ marginRight: 4 }} />;
}

interface PillProps {
  score: number;
  size?: "sm" | "md" | "lg";
  label: string;
  contextLabel?: string;
}

function ScorePill({ score, size = "md", label, contextLabel }: PillProps) {
  const band = bandFor(score);
  const tokens = tokensFor(band);
  const padding = size === "lg" ? "10px 18px" : size === "sm" ? "2px 10px" : "4px 14px";
  const fontSize = size === "lg" ? 22 : size === "sm" ? 13 : 16;
  const ariaLabel = contextLabel
    ? `${contextLabel}: score ${label} of 5, ${bandLabel(band)} (${tokens.label})`
    : `Score ${label} of 5, ${bandLabel(band)} (${tokens.label})`;
  return (
    <span
      role="img"
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        backgroundColor: tokens.bg,
        color: tokens.fg,
        borderRadius: 999,
        padding,
        fontSize,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {bandIcon(band)}
      <span>{label}</span>
    </span>
  );
}

export default function EvaluationReview({
  rubric,
  evaluation,
  aggregate,
  onEdit,
}: Props) {
  const overallMean = aggregate?.overallMean ?? null;
  const submittedAt = evaluation.submittedAt
    ? new Date(evaluation.submittedAt).toLocaleString()
    : null;
  const evaluator =
    evaluation.evaluatorName ||
    evaluation.evaluatorEmail ||
    evaluation.evaluatorUid;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title="Review evaluation"
        extra={
          <Button onClick={onEdit} aria-label="Edit evaluation">
            Edit evaluation
          </Button>
        }
      >
        <Space
          direction="vertical"
          size="middle"
          style={{ width: "100%" }}
        >
          <Space size="large" align="center" wrap>
            <div>
              <Typography.Text type="secondary" style={{ display: "block" }}>
                Overall score
              </Typography.Text>
              {overallMean !== null && overallMean > 0 ? (
                <ScorePill
                  score={overallMean}
                  size="lg"
                  label={overallMean.toFixed(2)}
                  contextLabel="Overall"
                />
              ) : (
                <Typography.Text>—</Typography.Text>
              )}
            </div>
            <div>
              <Typography.Text type="secondary" style={{ display: "block" }}>
                Submitted by
              </Typography.Text>
              <Typography.Text strong>{evaluator}</Typography.Text>
              {submittedAt ? (
                <Typography.Text
                  type="secondary"
                  style={{ display: "block", fontSize: 12 }}
                >
                  {submittedAt}
                </Typography.Text>
              ) : null}
            </div>
          </Space>

          <div>
            <Typography.Text
              type="secondary"
              style={{ display: "block", marginBottom: 8 }}
            >
              Category averages
            </Typography.Text>
            <Space wrap size="small">
              {rubric.categories.map((cat) => {
                const stats = aggregate?.categoryMeans?.[cat.id];
                if (!stats) {
                  return (
                    <Space key={cat.id} size={6}>
                      <Typography.Text>{cat.name}:</Typography.Text>
                      <Typography.Text type="secondary">—</Typography.Text>
                    </Space>
                  );
                }
                return (
                  <Space key={cat.id} size={6}>
                    <Typography.Text>{cat.name}:</Typography.Text>
                    <ScorePill
                      score={stats.mean}
                      label={stats.mean.toFixed(2)}
                      contextLabel={cat.name}
                    />
                  </Space>
                );
              })}
            </Space>
          </div>

          {evaluation.overallNotes ? (
            <div>
              <Typography.Text
                type="secondary"
                style={{ display: "block", marginBottom: 4 }}
              >
                Overall evaluator notes
              </Typography.Text>
              <Typography.Paragraph
                style={{ margin: 0, whiteSpace: "pre-wrap" }}
              >
                {evaluation.overallNotes}
              </Typography.Paragraph>
            </div>
          ) : null}
        </Space>
      </Card>

      <Collapse
        defaultActiveKey={rubric.categories.map((c) => c.id)}
        items={rubric.categories.map((category) => {
          const catStats = aggregate?.categoryMeans?.[category.id];
          return {
            key: category.id,
            label: (
              <Space wrap>
                <Typography.Text strong>{category.name}</Typography.Text>
                {catStats ? (
                  <ScorePill
                    score={catStats.mean}
                    size="sm"
                    label={`mean ${catStats.mean.toFixed(2)}`}
                    contextLabel={`${category.name} category`}
                  />
                ) : null}
              </Space>
            ),
            children: (
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                {category.criteria.map((criterion) => {
                  const critStats = aggregate?.criterionMeans?.[criterion.id];
                  return (
                    <Card
                      key={criterion.id}
                      size="small"
                      title={
                        <Space wrap>
                          <Typography.Text strong>{criterion.name}</Typography.Text>
                          {critStats ? (
                            <ScorePill
                              score={critStats.mean}
                              size="sm"
                              label={`mean ${critStats.mean.toFixed(2)}`}
                              contextLabel={`${criterion.name} criterion`}
                            />
                          ) : null}
                        </Space>
                      }
                    >
                      <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: "100%" }}
                      >
                        {(criterion.questions || []).map((question) => {
                          const entry = evaluation.scores?.[question.id];
                          return (
                            <div key={question.id}>
                              <Space align="start" wrap>
                                {entry ? (
                                  <ScorePill
                                    score={entry.score}
                                    label={String(entry.score)}
                                    contextLabel={question.text}
                                  />
                                ) : (
                                  <Typography.Text type="secondary">
                                    Not scored
                                  </Typography.Text>
                                )}
                                <Typography.Text>{question.text}</Typography.Text>
                              </Space>
                              {entry?.notes ? (
                                <Typography.Paragraph
                                  type="secondary"
                                  style={{
                                    marginTop: 4,
                                    marginBottom: 0,
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {entry.notes}
                                </Typography.Paragraph>
                              ) : null}
                            </div>
                          );
                        })}
                      </Space>
                    </Card>
                  );
                })}
                {category.criteria.length === 0 ? (
                  <Empty description="No criteria in this category." />
                ) : null}
              </Space>
            ),
          };
        })}
      />
    </Space>
  );
}
